import { useRtfInputState } from "@/zustand/RtfInputState";
import { MessageEntity } from "@/indexedDB/types";
import { RtfLinkType } from "@/middleware/links";

// TO DO => simplify the hook and remove the chatId parameter, since the chatId is already stored in memory state
// we can just reference it directly here.
// use the hook everywhere without havng to always pass the active chat

/**
 * Purpose: Encapsulate RTF input state management for a specific chat
 *
 * How it works:
 * 1. Takes a chatId and provides all RTF-related state and actions for that chat
 * 2. Centralizes access to draft, reply, edit, and link message states
 * 3. Provides type-safe accessor methods for all actions
 *
 * Key features:
 * - Simplified state access with a single hook call
 * - Automatically handles empty/undefined chatIdth
 * - Provides properly typed state and actions
 * - Reduces boilerplate in components
 */
export function useRtfInputForChat(chatId: string | null | undefined) {
  const safeChatId = chatId || "";

  // Get all state values for this chat
  const draftMessage = useRtfInputState(
    (state) => state.draftMessages[safeChatId],
  );
  const replyMessage = useRtfInputState(
    (state) => state.replyMessages[safeChatId],
  );
  const editMessage = useRtfInputState(
    (state) => state.editMessages[safeChatId],
  );
  const linkMessage = useRtfInputState(
    (state) => state.linkMessages[safeChatId],
  );
  const linkMessageMetadata = useRtfInputState(
    (state) => state.linkMessagesMetadata[safeChatId],
  );

  // Get all actions
  const setDraftMessage = useRtfInputState((state) => state.setDraftMessage);
  const clearDraftMessage = useRtfInputState(
    (state) => state.clearDraftMessage,
  );
  const setReplyMessage = useRtfInputState((state) => state.setReplyMessage);
  const clearReplyMessage = useRtfInputState(
    (state) => state.clearReplyMessage,
  );
  const setEditMessage = useRtfInputState((state) => state.setEditMessage);
  const clearEditMessage = useRtfInputState((state) => state.clearEditMessage);
  const setLinkMessage = useRtfInputState((state) => state.setLinkMessage);
  const clearLinkMessage = useRtfInputState((state) => state.clearLinkMessage);
  const setLinkMessageMetadata = useRtfInputState(
    (state) => state.setLinkMessageMetadata,
  );
  const clearLinkMessageMetadata = useRtfInputState(
    (state) => state.clearLinkMessageMetadata,
  );
  const resetRtfInputState = useRtfInputState(
    (state) => state.resetRtfInputState,
  );

  // Return wrapped methods that automatically use the current chatId
  return {
    // State
    draftMessage,
    replyMessage,
    editMessage,
    linkMessage,
    linkMessageMetadata,

    // Actions with pre-bound chatId
    setDraftMessage: (message: string) => setDraftMessage(safeChatId, message),
    clearDraftMessage: () => clearDraftMessage(safeChatId),
    setReplyMessage: (message: MessageEntity) =>
      setReplyMessage(safeChatId, message),
    clearReplyMessage: () => clearReplyMessage(safeChatId),
    setEditMessage: (message: MessageEntity) =>
      setEditMessage(safeChatId, message),
    clearEditMessage: () => clearEditMessage(safeChatId),
    setLinkMessage: (link: string) => setLinkMessage(safeChatId, link),
    clearLinkMessage: () => clearLinkMessage(safeChatId),
    setLinkMessageMetadata: (metadata: RtfLinkType) =>
      setLinkMessageMetadata(safeChatId, metadata),
    clearLinkMessageMetadata: () => clearLinkMessageMetadata(safeChatId),

    // Global action
    resetRtfInputState,

    // Utility to check if we have any active input state
    hasActiveInputState: !!(
      draftMessage ||
      replyMessage ||
      editMessage ||
      linkMessage
    ),
  };
}
